/**
 * Taille d'une tranche d'export. Choisie assez grande pour qu'un export courant
 * tienne en une ou deux requêtes : le throttler de l'API est à 20 requêtes par
 * minute et par utilisateur, partagées avec les requêtes du tableau lui-même.
 */
export const EXPORT_CHUNK_SIZE = 1000

/**
 * Plafond dur de l'export, soit 10 requêtes au pire. Au-delà, l'export s'arrête
 * et le prévient — avant, il tronquait en silence à 9999 lignes et l'admin
 * repartait avec un fichier incomplet sans le savoir.
 */
export const EXPORT_MAX_ROWS = 10_000

export type ExportPage<TRow> = {
  rows: TRow[]
  total: number
}

export type ExportResult<TRow> = {
  rows: TRow[]
  /** Total annoncé par le serveur, qui peut dépasser `rows.length`. */
  total: number
  /** Vrai si le plafond a coupé l'export avant la fin. */
  truncated: boolean
}

/**
 * Récupère toutes les lignes d'une requête paginée, tranche par tranche,
 * jusqu'au total annoncé par le serveur ou jusqu'à `EXPORT_MAX_ROWS`.
 *
 * Renvoie `null` si une tranche ne répond pas : mieux vaut pas de fichier
 * qu'un fichier amputé de sa moitié sans le dire.
 */
export async function fetchAllForExport<TRow>(
  fetchPage: (skip: number, take: number) => Promise<ExportPage<TRow> | null>,
): Promise<ExportResult<TRow> | null> {
  const rows: TRow[] = []
  let total = 0

  while (rows.length < EXPORT_MAX_ROWS) {
    const take = Math.min(EXPORT_CHUNK_SIZE, EXPORT_MAX_ROWS - rows.length)
    const page = await fetchPage(rows.length, take)
    if (!page) return null

    total = page.total
    rows.push(...page.rows)

    // Une tranche vide signe la fin des données, même si le total annoncé est
    // plus grand : sans cette garde, un total périmé boucle indéfiniment.
    if (page.rows.length === 0 || rows.length >= total) break
  }

  return { rows, total, truncated: rows.length < total }
}
